/**
 * Zustand store for the Business Inbox
 */

import { create } from 'zustand'
import { arrivedIds as nextArrivedIds } from '@/components/inbox/newArrivals'
import type { QueueItem, TriageRun, TriageRunItem } from '@/types/inbox'
import type {
  InboxConversationSummary,
  InboxConversationDetail,
  InboxStats,
  InboxViewTab,
} from '@/types/inbox'

/** Server page size — fetch/append math and the head-merge cutoff use this. */
export const INBOX_PAGE_SIZE = 50

/** What a channel chip change writes beside the chip (`inboxShape.chipWrites`). */
export type ChannelChangeWrites = Partial<Pick<InboxState, 'statusFilter' | 'faultsView' | 'sortBy'>>

interface InboxState {
  // State
  conversationList: InboxConversationSummary[]
  currentConversation: InboxConversationDetail | null
  stats: InboxStats | null
  total: number
  isLoading: boolean
  isLoadingDetail: boolean
  isLoadingMore: boolean
  loadMoreError: string | null
  error: string | null
  /** The buildListParams(0) string the loaded pages answer; fetchConversations's `keep` reads it. */
  loadedParams: string | null

  // Filters
  viewTab: InboxViewTab
  channelFilter: string | null
  statusFilter: string | null
  /** SA-1091: the chip row's selections (the list's `kind=`, `flag=`,
   * `mailbox_id=`). Each a toggle; a tab switch keeps them, as the channel. */
  kindFilter: string | null
  flagFilter: string | null
  mailboxFilter: string | null
  personaFilter: string | null
  searchQuery: string
  sortBy: string
  faultsView: boolean

  /** SA-1092 §3: the Triage tab's queue, its own slice (the list's rows are threads). */
  queue: { items: QueueItem[]; total: number; arrivedIds: string[]; isLoading: boolean; error: string | null }
  setQueue: (items: QueueItem[], total: number) => void
  mergeQueueHead: (items: QueueItem[], total: number) => void
  appendQueue: (items: QueueItem[], total: number) => void
  setQueueLoading: (loading: boolean) => void
  setQueueError: (error: string | null) => void
  clearQueueArrivals: () => void

  /** SA-1329: the bulk triage run in progress or just ended (services/triageRun). */
  triageRun: TriageRun | null
  beginTriageRun: (run: TriageRun) => void
  /** A settle (state leaving queued/deciding) also adds 1 to `settled`. No-op for another run's id. */
  patchTriageRunItem: (runId: string, key: string, patch: Partial<TriageRunItem>) => void
  finishTriageRun: (runId: string) => void
  clearTriageRun: () => void

  // Actions
  setConversationList: (conversations: InboxConversationSummary[], total: number) => void
  appendConversations: (conversations: InboxConversationSummary[], total: number) => void
  mergeConversationHead: (fresh: InboxConversationSummary[], total: number) => void
  removeConversationFromList: (id: string) => void
  setCurrentConversation: (conversation: InboxConversationDetail | null) => void
  setStats: (stats: InboxStats | null) => void
  setLoading: (loading: boolean) => void
  setLoadingDetail: (loading: boolean) => void
  setLoadingMore: (loading: boolean) => void
  setLoadMoreError: (error: string | null) => void
  setError: (error: string | null) => void
  setViewTab: (tab: InboxViewTab) => void
  /** SA-1104: `extra` rides the same write (a union's chip change drops the
   * console's selections and takes the chip's sort), so the list refetches
   * once with the final parameters. */
  setChannelFilter: (channel: string | null, extra?: ChannelChangeWrites) => void
  setStatusFilter: (status: string | null) => void
  setKindFilter: (kind: string | null) => void
  setFlagFilter: (flag: string | null) => void
  setMailboxFilter: (mailboxId: string | null) => void
  /** SA-1091: the chip row's Clear — kind, flag and mailbox only. */
  clearTriageFilters: () => void
  setPersonaFilter: (persona: string | null) => void
  setSearchQuery: (query: string) => void
  setSortBy: (sort: string) => void
  setLoadedParams: (params: string | null) => void
  setFaultsView: (faultsView: boolean) => void
  updateConversationInList: (id: string, updates: Partial<InboxConversationSummary>) => void
  decrementUnread: (amount: number) => void
}

export const useInboxStore = create<InboxState>((set) => ({
  conversationList: [],
  currentConversation: null,
  stats: null,
  total: 0,
  loadedParams: null,
  isLoading: false,
  isLoadingDetail: false,
  isLoadingMore: false,
  loadMoreError: null,
  error: null,
  viewTab: 'open',
  channelFilter: null,
  statusFilter: null,
  kindFilter: null,
  flagFilter: null,
  mailboxFilter: null,
  personaFilter: null,
  searchQuery: '',
  sortBy: 'recent',
  faultsView: false,
  queue: { items: [], total: 0, arrivedIds: [], isLoading: false, error: null },

  setQueue: (items, total) => set((s) => ({ queue: { ...s.queue, items, total, arrivedIds: [], error: null } })),
  mergeQueueHead: (items, total) => set((s) => {
    const arrivedIds = nextArrivedIds(s.queue.items.map((i) => i.id), items.map((i) => i.id), s.queue.arrivedIds)
    if (s.queue.items.length <= INBOX_PAGE_SIZE) return { queue: { ...s.queue, items, total, arrivedIds, error: null } }
    const seen = new Set(items.map((i) => i.id))
    const tail = s.queue.items.slice(INBOX_PAGE_SIZE).filter((i) => !seen.has(i.id))
    return { queue: { ...s.queue, items: [...items, ...tail], total, arrivedIds, error: null } }
  }),
  appendQueue: (items, total) => set((s) => {
    const seen = new Set(s.queue.items.map((i) => i.id))
    return { queue: { ...s.queue, items: [...s.queue.items, ...items.filter((i) => !seen.has(i.id))], total } }
  }),
  setQueueLoading: (isLoading) => set((s) => ({ queue: { ...s.queue, isLoading } })),
  setQueueError: (error) => set((s) => ({ queue: { ...s.queue, error } })),
  clearQueueArrivals: () => set((s) => ({ queue: { ...s.queue, arrivedIds: [] } })),

  setConversationList: (conversations, total) =>
    set({ conversationList: conversations, total }),
  appendConversations: (conversations, total) =>
    set((state) => {
      const seen = new Set(state.conversationList.map((c) => c.id))
      const fresh = conversations.filter((c) => !seen.has(c.id))
      return { conversationList: [...state.conversationList, ...fresh], total }
    }),
  mergeConversationHead: (fresh, total) =>
    set((state) => {
      // One page or less loaded: plain replace (pre-pagination semantics —
      // rows that no longer match the view drop out immediately).
      if (state.conversationList.length <= INBOX_PAGE_SIZE) {
        return { conversationList: fresh, total }
      }
      // Deep scroll: fresh page becomes the head; keep the already-loaded
      // tail (deduped) so a silent SSE refresh can't truncate the scroll.
      const freshIds = new Set(fresh.map((c) => c.id))
      const tail = state.conversationList.filter((c) => !freshIds.has(c.id))
      return { conversationList: [...fresh, ...tail], total }
    }),
  removeConversationFromList: (id) =>
    set((state) => {
      const existed = state.conversationList.some((c) => c.id === id)
      return {
        conversationList: state.conversationList.filter((c) => c.id !== id),
        total: existed ? Math.max(0, state.total - 1) : state.total,
      }
    }),
  setCurrentConversation: (conversation) =>
    set({ currentConversation: conversation }),
  setStats: (stats) => set({ stats }),
  setLoading: (loading) => set({ isLoading: loading }),
  setLoadingDetail: (loading) => set({ isLoadingDetail: loading }),
  setLoadingMore: (loading) => set({ isLoadingMore: loading }),
  setLoadMoreError: (error) => set({ loadMoreError: error }),
  setError: (error) => set({ error }),
  setViewTab: (tab) => set({ viewTab: tab, statusFilter: null, loadMoreError: null }),
  setChannelFilter: (channel, extra = {}) => set({ channelFilter: channel, ...extra, loadMoreError: null }),
  setStatusFilter: (status) => set({ statusFilter: status }),
  setKindFilter: (kind) => set({ kindFilter: kind }),
  setFlagFilter: (flag) => set({ flagFilter: flag }),
  setMailboxFilter: (mailboxId) => set({ mailboxFilter: mailboxId }),
  clearTriageFilters: () => set({ kindFilter: null, flagFilter: null, mailboxFilter: null }),
  setPersonaFilter: (persona) => set({ personaFilter: persona }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSortBy: (sort) => set({ sortBy: sort }),
  setLoadedParams: (params) => set({ loadedParams: params }),
  setFaultsView: (faultsView) => set({ faultsView, loadMoreError: null }),
  updateConversationInList: (id, updates) =>
    set((state) => ({
      conversationList: state.conversationList.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),
  triageRun: null,
  beginTriageRun: (run) => set({ triageRun: run }),
  patchTriageRunItem: (runId, key, patch) =>
    set((state) => {
      const run = state.triageRun
      if (!run || run.id !== runId) return {}
      const open = (s: TriageRunItem['state']) => s === 'queued' || s === 'deciding'
      let settled = run.settled
      const items = run.items.map((item) => {
        if (item.key !== key) return item
        const next = { ...item, ...patch }
        if (open(item.state) && !open(next.state)) settled += 1
        return next
      })
      return { triageRun: { ...run, items, settled } }
    }),
  finishTriageRun: (runId) =>
    set((state) =>
      state.triageRun?.id === runId ? { triageRun: { ...state.triageRun, status: 'done' } } : {},
    ),
  clearTriageRun: () => set({ triageRun: null }),
  decrementUnread: (amount) =>
    set((state) => ({
      stats: state.stats
        ? { ...state.stats, totalUnread: Math.max(0, state.stats.totalUnread - amount) }
        : state.stats,
    })),
}))
